import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { SettingsTab } from '../contexts/SettingsDialogContext';
import ProfileTab from './user/ProfileTab';
import BillingSubscriptionTab from './user/BillingSubscriptionTab';

export interface UserInfo {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
  emailConfirmed: boolean;
  phoneNumber: string | null;
  phoneNumberConfirmed: boolean;
  defaultBusinessId: string | null;
  defaultDeviceId: string | null;
  themePreference: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  initialTab?: SettingsTab;
}

const TAB_ORDER: SettingsTab[] = ['profile', 'billing'];

const UserSettingsDialog: React.FC<Props> = ({ open, onClose, initialTab = 'profile' }) => {
  const auth = useAuth();
  const authHeader = { Authorization: `Bearer ${auth.tokens?.accessToken}` };

  const [tab, setTab] = useState<SettingsTab>(initialTab);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserInfo = () => {
    setLoading(true);
    setError(null);
    return fetch('/api/users/me', { headers: authHeader })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load settings');
        return res.json();
      })
      .then((data: UserInfo) => setUserInfo(data))
      .catch(() => setError('Failed to load settings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    fetchUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Settings</DialogTitle>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="settings tabs">
          <Tab label="Profile" value="profile" />
          <Tab label="Billing & Subscription" value="billing" />
        </Tabs>
      </Box>
      <DialogContent>
        {loading || !userInfo ? (
          <Box display="flex" justifyContent="center" py={4}>
            {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            {TAB_ORDER.map((t) => (
              <Box key={t} sx={{ display: tab === t ? 'block' : 'none' }}>
                {t === 'profile' && (
                  <ProfileTab
                    userInfo={userInfo}
                    onUserInfoChange={setUserInfo}
                    onClose={onClose}
                  />
                )}
                {t === 'billing' && <BillingSubscriptionTab userInfo={userInfo} />}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserSettingsDialog;
